import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronLeft, Settings, CircleUser, Camera, Pencil, CalendarDays, Crown,
  ChevronDown, Gem, Gift, Rocket, Headphones, X, Mail, Phone, Lock, LogOut,
  Sparkles, Star, Zap, Trophy, Heart, Award, Medal, ShieldCheck, BadgeCheck,
  Coins, DollarSign, Clock, Flame, Copy, Check, Upload,
} from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { renderInlineMarkdown, parseBenefit } from '../../../lib/inlineMarkdown';
import { tap } from './playerHelpers';
import SafeImage from '../../../components/SafeImage';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/* -------------------- BENEFIT ICON SHORTCODES (Iter 145+) --------------------
 * Admins can prefix a benefit with `[shortcode]` (e.g. "[gift] 10% deposit
 * bonus") to render a polished lucide icon instead of an emoji or bullet.
 * Add new mappings here as needed — they're rendered by `BenefitRow` below.
 */
const BENEFIT_ICONS = {
  gift: Gift, sparkles: Sparkles, star: Star, zap: Zap, trophy: Trophy,
  heart: Heart, award: Award, medal: Medal, shield: ShieldCheck, verified: BadgeCheck,
  crown: Crown, gem: Gem, rocket: Rocket, support: Headphones, headphones: Headphones,
  coins: Coins, dollar: DollarSign, money: DollarSign, clock: Clock,
  fast: Zap, priority: Zap, flame: Flame, fire: Flame,
};

const PROFILE_ACCENT = 'var(--vl-profile-accent, var(--vl-neon, #004D61))';
const PROFILE_ACCENT_TEXT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 70%, #FFFFFF)';
const PROFILE_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 16%, transparent)';
const PROFILE_ACCENT_SOFT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 24%, transparent)';
const PROFILE_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 46%, transparent)';
const PROFILE_ACCENT_STRONG_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 72%, transparent)';
const PROFILE_ACCENT_GLOW = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 42%, transparent)';
const PROFILE_ACCENT_GRADIENT = 'linear-gradient(135deg, var(--vl-btn-primary-from, var(--vl-profile-accent, var(--vl-neon, #004D61))), var(--vl-btn-primary-to, #004D61))';
const PROFILE_CARD_GRADIENT = 'linear-gradient(135deg, color-mix(in oklab, var(--vl-bg, #000000) 76%, var(--vl-profile-accent, var(--vl-neon, #004D61))), color-mix(in oklab, var(--vl-bg, #000000) 91%, var(--vl-profile-accent, var(--vl-neon, #004D61))))';
const PROFILE_ACTION_BG = 'color-mix(in oklab, var(--vl-bg, #000000) 88%, #FFFFFF 12%)';
const PROFILE_ACTION_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 42%, #FFFFFF 18%)';
const PROFILE_GOLD = 'var(--vl-brand-primary, #D4AF37)';
const OCEAN_AVATAR_SPRITE = '/assets/player-v2/mascots/slots-oceans-avatar-collection-v1.png';
const OCEAN_AVATARS = [
  { id: 'wave-rider', name: 'Wave Rider', x: 0, y: 0 },
  { id: 'coral-bloom', name: 'Coral Bloom', x: 1, y: 0 },
  { id: 'midnight-fin', name: 'Midnight Fin', x: 2, y: 0 },
  { id: 'hammer-bay', name: 'Hammer Bay', x: 3, y: 0 },
  { id: 'reef-pilot', name: 'Reef Pilot', x: 0, y: 1 },
  { id: 'sunny-stripe', name: 'Sunny Stripe', x: 1, y: 1 },
  { id: 'blue-current', name: 'Blue Current', x: 2, y: 1 },
  { id: 'mint-bubble', name: 'Mint Bubble', x: 3, y: 1 },
  { id: 'violet-tide', name: 'Violet Tide', x: 0, y: 2 },
  { id: 'coral-glow', name: 'Coral Glow', x: 1, y: 2 },
  { id: 'ocean-captain', name: 'Ocean Captain', x: 2, y: 2 },
];

const oceanAvatarSpriteStyle = (avatar) => ({
  backgroundImage: `url(${OCEAN_AVATAR_SPRITE})`,
  backgroundPosition: `${avatar.x * (100 / 3)}% ${avatar.y * 50}%`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '400% 300%',
});

const cropOceanAvatar = (avatar) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => {
    const columns = 4;
    const rows = 3;
    const sourceWidth = image.naturalWidth / columns;
    const sourceHeight = image.naturalHeight / rows;
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 320;
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('Avatar renderer is unavailable'));
      return;
    }
    context.drawImage(
      image,
      avatar.x * sourceWidth,
      avatar.y * sourceHeight,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    resolve(canvas.toDataURL('image/jpeg', 0.9));
  };
  image.onerror = () => reject(new Error('Avatar collection could not be loaded'));
  image.src = OCEAN_AVATAR_SPRITE;
});

const defaultNameFromEmail = (value = '') => {
  const prefix = String(value || '').split('@')[0] || '';
  const first = prefix.match(/[a-z0-9]/i)?.[0] || '';
  return first ? first.toUpperCase() : '';
};

const FALLBACK_TIERS = [
  { key: 'bronze', name: 'Bronze', level: 1, min_xp: 0, max_xp: 1000, min_referrals: 0, emoji: 'B', color: '#CD7F32', sort_order: 0, perks: ['Standard support'] },
  { key: 'silver', name: 'Silver', level: 2, min_xp: 1000, max_xp: 3000, min_referrals: 1, emoji: 'S', color: '#C0C0C0', sort_order: 1, perks: ['Priority support'] },
  { key: 'gold', name: 'Gold', level: 3, min_xp: 3000, max_xp: 7500, min_referrals: 3, emoji: 'G', color: '#D4AF37', sort_order: 2, perks: ['VIP rewards'] },
];

const normalizeTierKey = (value = '') => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const tierNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const formatRemainingWindow = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '';
  const hours = Math.floor(n / 3600);
  const minutes = Math.ceil((n % 3600) / 60);
  if (hours <= 0) return `${minutes}m left`;
  return `${hours}h ${minutes}m left`;
};
const resolveProfileUrl = (url = '') => {
  const raw = String(url || '').trim();
  if (!raw || /^(https?:|data:|blob:)/i.test(raw)) return raw;
  return `${API_BASE}${raw}`;
};
const normalizeTierRow = (tier = {}, index = 0) => {
  const name = tier.name || tier.tier_name || 'Member';
  const minXp = tierNumber(tier.min_xp ?? tier.min_sc ?? tier.points_required ?? tier.pointsToNext, 0);
  const maxXp = tierNumber(tier.max_xp ?? tier.max_sc, 0);
  const level = tierNumber(tier.level, tierNumber(tier.sort_order, index) + 1);
  const color = tier.color || tier.tier_color || tier.crystal || PROFILE_GOLD;
  return {
    ...tier,
    key: tier.key || normalizeTierKey(name),
    name,
    level,
    minXp,
    maxXp,
    minReferrals: tierNumber(tier.min_referrals, 0),
    dailyBonusPct: tierNumber(tier.daily_bonus_pct, 0),
    color,
    crystal: color,
    gradient: tier.gradient || `linear-gradient(135deg, ${color}, color-mix(in oklab, ${color} 45%, #020617))`,
    icon: tier.emoji || tier.icon || name.charAt(0).toUpperCase(),
    logoUrl: tier.logo_url || '',
    perks: Array.isArray(tier.perks) ? tier.perks : [],
    benefits: Array.isArray(tier.benefits) ? tier.benefits : [],
    sort_order: tierNumber(tier.sort_order, index),
  };
};

function BenefitRow({ raw, accent = PROFILE_ACCENT }) {
  const { icon, text } = parseBenefit(raw);
  // Resolve a lucide icon when admin used `[shortcode]`.
  let LucideIcon = null;
  if (icon && icon.startsWith('[') && icon.endsWith(']')) {
    LucideIcon = BENEFIT_ICONS[icon.slice(1, -1).toLowerCase()];
  }
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-xl"
      style={{ background: `color-mix(in oklab, ${accent} 10%, transparent)`, border: `1px solid color-mix(in oklab, ${accent} 25%, transparent)` }}
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0"
        style={{ background: `color-mix(in oklab, ${accent} 20%, transparent)`, color: accent }}
        aria-hidden
      >
        {LucideIcon ? <LucideIcon className="w-4 h-4" /> : (icon || '•')}
      </span>
      <span className="text-sm leading-snug" style={{ color: '#E5E7EB' }}>{text}</span>
    </div>
  );
}

/* -------------------- TIER BENEFIT BADGE -------------------- */
function TierBenefit({ icon: Icon, label, color = PROFILE_ACCENT }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid color-mix(in oklab, ${color} 34%, transparent)`, boxShadow: `0 0 14px color-mix(in oklab, ${color} 20%, transparent)` }}
      >
        <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.2} />
      </span>
      <p className="text-[10px] font-semibold leading-tight text-center" style={{ color: PROFILE_ACCENT_TEXT }}>{label}</p>
    </div>
  );
}

/* -------------------- SETTINGS CARD (used in Security & Account) -------------------- */
function SettingsCard({ icon: Icon, iconBg, iconClassName = 'w-5 h-5', title, subtitle, onClick, testid }) {
  return (
    <button
      onClick={() => { tap(); onClick?.(); }}
      className="w-full flex items-center gap-4 rounded-2xl px-4 py-4 mt-3 transition active:scale-[0.99] hover:brightness-110 text-left"
      style={{
        background: PROFILE_ACCENT_DIM,
        border: `1px solid ${PROFILE_ACCENT_BORDER}`,
        boxShadow: `0 0 18px ${PROFILE_ACCENT_GLOW}`,
      }}
      data-testid={testid}
    >
      <span
        className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
        style={{
          background: iconBg || PROFILE_ACCENT_GRADIENT,
          border: '1.5px solid rgba(255,255,255,0.36)',
          outline: `1px solid ${PROFILE_ACCENT_STRONG_BORDER}`,
          outlineOffset: '2px',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
        }}
        aria-hidden
      >
        <Icon className={iconClassName} style={{ color: '#FFFFFF' }} strokeWidth={2.35} />
      </span>
      <div className="flex-1 min-w-0">
        <p
          className="font-extrabold text-base sm:text-lg leading-tight"
          style={{ color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif' }}
        >
          {title}
        </p>
        <p
          className="text-sm leading-tight mt-0.5 truncate"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          {subtitle}
        </p>
      </div>
      <ChevronDown className="w-5 h-5 -rotate-90 shrink-0" style={{ color: PROFILE_ACCENT_TEXT }} />
    </button>
  );
}

function VerifiedUserBadge({ size = 'normal', className = '' }) {
  const dims = size === 'small' ? 'w-4 h-4' : 'w-5 h-5';
  const iconSize = size === 'small' ? 10 : 12;
  return (
    <span
      className={`${dims} rounded-full inline-flex items-center justify-center shrink-0 ${className}`}
      style={{
        background: 'radial-gradient(circle at 30% 25%, #FFF4B8 0%, var(--vl-brand-primary, #D4AF37) 48%, #8B5E00 100%)',
        border: '2px solid #0B0F0C',
        boxShadow: '0 0 8px color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 72%, transparent), inset 0 1px 1px rgba(255,255,255,0.55)',
      }}
      aria-hidden="true"
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function OceanAvatarThumb({ avatar, size = 74 }) {
  return (
    <span
      className="block shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        ...oceanAvatarSpriteStyle(avatar),
        border: '2px solid rgba(79, 226, 239, 0.58)',
        boxShadow: '0 8px 22px rgba(0, 8, 18, 0.34), 0 0 0 3px rgba(65, 218, 233, 0.07)',
      }}
      aria-hidden="true"
    />
  );
}

function OceanAvatarPicker({ currentAvatarUrl, selectedId, busy, onSelect, onUpload, onClose, fileInputRef }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-5"
      role="presentation"
      onMouseDown={onClose}
      data-testid="profile-avatar-picker-backdrop"
    >
      <section
        className="max-h-[94dvh] w-full max-w-xl overflow-y-auto rounded-t-[28px] border p-4 pb-[calc(18px+env(safe-area-inset-bottom))] text-white shadow-2xl sm:rounded-[28px] sm:p-6"
        style={{
          borderColor: 'rgba(74, 224, 238, 0.32)',
          background: 'radial-gradient(circle at 90% 0, rgba(51, 220, 235, 0.18), transparent 32%), radial-gradient(circle at 0 100%, rgba(39, 185, 162, 0.12), transparent 34%), #061925',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ocean-avatar-picker-title"
        onMouseDown={(event) => event.stopPropagation()}
        data-testid="profile-avatar-picker"
      >
        <header className="flex items-start justify-between gap-4">
          <div>
            <p className="m-0 text-[10px] font-extrabold uppercase tracking-[0.16em] text-cyan-300">Make it yours</p>
            <h2 id="ocean-avatar-picker-title" className="display mt-1 text-2xl font-extrabold tracking-tight">Choose your ocean avatar.</h2>
            <p className="mt-2 max-w-md text-xs leading-relaxed text-slate-400">Pick a Slots Oceans shark or reef character, or upload your own photo.</p>
          </div>
          <button type="button" onClick={onClose} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-cyan-200/20 bg-white/[0.04] text-slate-300" aria-label="Close avatar picker">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-cyan-200/20 bg-cyan-300/[0.06] p-3">
          <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-cyan-300/50 bg-slate-900">
            {currentAvatarUrl
              ? <SafeImage src={currentAvatarUrl} alt="Current avatar" className="h-full w-full object-cover" fallback={<CircleUser className="h-7 w-7 text-white" />} />
              : <CircleUser className="h-7 w-7 text-white" />}
          </span>
          <span className="min-w-0 flex-1"><small className="block text-[10px] text-slate-400">Current profile</small><strong className="mt-1 block truncate text-sm">{selectedId ? OCEAN_AVATARS.find((avatar) => avatar.id === selectedId)?.name : currentAvatarUrl ? 'Your photo' : 'Choose an avatar'}</strong></span>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-cyan-300/30 bg-cyan-300/[0.08] px-3 text-xs font-extrabold text-cyan-50 disabled:opacity-50">
            <Upload className="h-4 w-4" /> Upload photo
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onUpload} className="hidden" data-testid="profile-fs-photo-input" />
        </div>

        <div className="mb-3 mt-5 flex items-center justify-between"><strong className="text-xs">Slots Oceans characters</strong><small className="text-[10px] text-slate-500">11 avatars</small></div>
        <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6" data-testid="profile-ocean-avatar-grid">
          {OCEAN_AVATARS.map((avatar) => {
            const selected = avatar.id === selectedId;
            return (
              <button
                key={avatar.id}
                type="button"
                onClick={() => onSelect(avatar)}
                disabled={busy}
                className="relative grid min-w-0 justify-items-center gap-2 rounded-2xl border px-1.5 py-2.5 transition active:scale-95 disabled:opacity-55"
                style={{ borderColor: selected ? 'rgba(80, 232, 241, 0.75)' : 'rgba(121, 171, 184, 0.18)', background: selected ? 'rgba(57, 215, 230, 0.11)' : 'rgba(255,255,255,0.018)' }}
                aria-label={`Use ${avatar.name} avatar`}
                aria-pressed={selected}
              >
                <OceanAvatarThumb avatar={avatar} size={64} />
                <span className="w-full truncate text-center text-[9px] font-bold text-slate-300">{avatar.name}</span>
                {selected && <i className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-cyan-300 text-slate-950"><Check className="h-3 w-3" strokeWidth={3} /></i>}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={onClose} className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-cyan-300 text-sm font-black text-slate-950">
          Done <Check className="h-4 w-4" />
        </button>
      </section>
    </div>
  );
}

/* -------------------- PROFILE FULL-SCREEN -------------------- */
export default function ProfileFullScreen({
  user,
  branding,
  badge,
  settingsOpen = false,
  onClose,
  onOpenSettings,
  onCloseSettings,
  onEditProfile,
  onEditPhone,
  onResetPassword,
  onLogout,
}) {
  const { checkAuth } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [selectedOceanAvatarId, setSelectedOceanAvatarId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(`slots-oceans-avatar:${user?.id || user?.email || 'player'}`) || null;
  });
  const [avatarTipVisible, setAvatarTipVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('slots-oceans-avatar-tip-seen') !== 'true';
  });
  const [stats, setStats] = useState(null);
  const [tierRows, setTierRows] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [tierDocs, setTierDocs] = useState({});
  const [tierDetail, setTierDetail] = useState(null); // currently-shown tier in detail modal
  const [agentCodeStatus, setAgentCodeStatus] = useState(null);
  const [agentCodeOpen, setAgentCodeOpen] = useState(false);
  const [agentCodeDraft, setAgentCodeDraft] = useState('');
  const [agentCodeLoading, setAgentCodeLoading] = useState(false);
  const [phoneRewardStatus, setPhoneRewardStatus] = useState(null);

  const refreshAgentCodeStatus = async () => {
    try {
      const res = await api.get('/api/agent-earn/player/code-status');
      setAgentCodeStatus(res.data || null);
    } catch {
      setAgentCodeStatus(null);
    }
  };

  useEffect(() => {
    api.get('/api/referrals/stats')
      .then(r => { if (r.data) setStats(r.data); })
      .catch(() => {});
    api.get('/api/tiers')
      .then(r => {
        const list = r.data?.tiers || r.data || [];
        setTierRows(Array.isArray(list) ? list : []);
        const map = {};
        list.forEach((t) => {
          const k = (t.name || '').toLowerCase();
          if (k) map[k] = t;
        });
        setTierDocs(map);
      })
      .catch(() => {});
    api.get('/api/tiers/my-tier')
      .then(r => setTierInfo(r.data || null))
      .catch(() => setTierInfo(null))
      .finally(() => setTiersLoading(false));
    api.get('/api/users/me/phone-verification/status')
      .then(r => setPhoneRewardStatus(r.data || null))
      .catch(() => setPhoneRewardStatus(null));
    refreshAgentCodeStatus();
  }, []);

  const phoneRewardAmount = Number(phoneRewardStatus?.mobile_verification_reward_fp ?? 0);
  const phoneRewardSubtitle = phoneRewardStatus === null
    ? 'Add and verify your phone number'
    : phoneRewardStatus?.mobile_verification_reward_enabled !== false && phoneRewardAmount > 0
      ? `Add your phone to claim ${phoneRewardAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} FP`
      : 'Add and verify your phone number';

  // Removed duplicate useEffect above (keeps single source of truth)
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max image size is 5 MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const max = 320;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.patch('/api/auth/profile', { avatar_url: dataUrl });
      window.localStorage.removeItem(`slots-oceans-avatar:${user?.id || user?.email || 'player'}`);
      setSelectedOceanAvatarId(null);
      await checkAuth();
      setAvatarPickerOpen(false);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally { setUploading(false); }
  };

  const selectOceanAvatar = async (avatar) => {
    if (!avatar || uploading) return;
    tap();
    setUploading(true);
    try {
      const dataUrl = await cropOceanAvatar(avatar);
      await api.patch('/api/auth/profile', { avatar_url: dataUrl });
      window.localStorage.setItem(`slots-oceans-avatar:${user?.id || user?.email || 'player'}`, avatar.id);
      setSelectedOceanAvatarId(avatar.id);
      await checkAuth();
      setAvatarPickerOpen(false);
      toast.success(`${avatar.name} is now your profile avatar`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Avatar could not be updated');
    } finally {
      setUploading(false);
    }
  };

  const dismissAvatarTip = () => {
    setAvatarTipVisible(false);
    window.localStorage.setItem('slots-oceans-avatar-tip-seen', 'true');
  };

  const openAvatarPicker = () => {
    if (uploading) return;
    tap();
    dismissAvatarTip();
    setAvatarPickerOpen(true);
  };

  const avatarUrl = user?.avatar_url;
  const tiers = (tierRows.length ? tierRows : FALLBACK_TIERS)
    .map((t, i) => normalizeTierRow(t, i))
    .sort((a, b) => a.sort_order - b.sort_order);
  const currentTierFromApi = tierInfo?.current_tier ? normalizeTierRow(tierInfo.current_tier) : null;
  const currentTierKey = normalizeTierKey(tierInfo?.tier_name || user?.achievement_tier || user?.tier || currentTierFromApi?.name || tiers[0]?.name);
  const currentTier = tiers.find(t => t.key === currentTierKey || normalizeTierKey(t.name) === currentTierKey)
    || currentTierFromApi
    || tiers[0]
    || normalizeTierRow(FALLBACK_TIERS[0]);
  const nextTier = tierInfo?.next_tier
    ? normalizeTierRow(tierInfo.next_tier, currentTier.level)
    : tiers.find(t => t.level > currentTier.level);
  const currentPoints = tierNumber(tierInfo?.current_xp ?? tierInfo?.total_deposit_sc ?? user?.total_xp_earned ?? stats?.tier_points, 0);
  const currentMinXp = tierNumber(currentTier.minXp, 0);
  const nextMinXp = nextTier ? tierNumber(nextTier.minXp, currentMinXp) : currentMinXp;
  const tierProgress = tierInfo?.progress_percent !== undefined
    ? Math.max(0, Math.min(100, tierNumber(tierInfo.progress_percent, 0)))
    : (nextTier ? Math.max(0, Math.min(100, Math.round(((currentPoints - currentMinXp) / Math.max(1, nextMinXp - currentMinXp)) * 100))) : 100);
  const xpToNext = nextTier ? Math.max(0, tierNumber(tierInfo?.xp_to_next ?? tierInfo?.xp_needed, nextMinXp - currentPoints)) : 0;
  const referralsNeeded = nextTier ? Math.max(0, tierNumber(tierInfo?.referrals_needed, nextTier.minReferrals - tierNumber(tierInfo?.referral_count ?? stats?.referral_count, 0))) : 0;
  const tierBenefitPreview = (currentTier.benefits?.length ? currentTier.benefits : currentTier.perks || []).slice(0, 2);
  const memberSinceRaw = user?.created_at || user?.member_since;
  const memberSince = memberSinceRaw ? new Date(memberSinceRaw).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;
  const profileName = (user?.full_name || '').trim();
  const defaultProfileName = defaultNameFromEmail(user?.email);
  const visibleProfileName = profileName || defaultProfileName;
  const username = (user?.username || '').trim();
  const usernameLabel = username ? `@${username}` : 'No username set';
  const showVerifiedBadge = !!badge?.show_badge;
  const nameInputRef = useRef(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(visibleProfileName);
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!editingName) setNameDraft(visibleProfileName);
  }, [editingName, visibleProfileName]);

  useEffect(() => {
    if (!editingName) return;
    const t = setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 40);
    return () => clearTimeout(t);
  }, [editingName]);

  const startNameEdit = () => {
    setNameDraft(visibleProfileName);
    setEditingName(true);
  };

  const saveProfileName = async () => {
    if (savingName) return;
    const nextName = (nameDraft || '').trim();
    if (!nextName) {
      setNameDraft(visibleProfileName);
      setEditingName(false);
      return;
    }
    if (nextName === profileName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await api.patch('/api/auth/profile', { full_name: nextName });
      await checkAuth();
      toast.success('Name updated');
      setEditingName(false);
    } catch (err) {
      setNameDraft(visibleProfileName);
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const copyUsername = async () => {
    if (!username) {
      toast.info('Set a username first');
      onEditProfile?.();
      return;
    }
    try {
      await navigator.clipboard.writeText(username);
      toast.success('Username copied');
    } catch {
      toast.error('Could not copy username');
    }
  };

  const showVerifiedUserToast = (e) => {
    e?.stopPropagation?.();
    tap();
    toast.success('Verified user');
  };

  const [viewAllTiers, setViewAllTiers] = useState(false);
  const agentName = agentCodeStatus?.agent?.display_name || agentCodeStatus?.agent?.name || 'your agent';
  const agentWindowText = formatRemainingWindow(agentCodeStatus?.seconds_remaining);
  const agentCodeSubtitle = agentCodeStatus?.connected
    ? `Connected to ${agentName}`
    : agentCodeStatus?.eligible === false
      ? 'Agent connection-code window expired'
      : agentWindowText
        ? `Connect within ${agentWindowText}`
        : 'Connect to an agent within 24 hours';

  const openAgentCodeModal = () => {
    if (agentCodeStatus?.connected) {
      toast.success(`Already connected to ${agentName}`);
      return;
    }
    if (agentCodeStatus?.eligible === false) {
      toast.error('Agent connection codes are available for 24 hours after signup');
      return;
    }
    setAgentCodeOpen(true);
  };

  const submitAgentCode = async (event) => {
    event.preventDefault();
    const code = agentCodeDraft.trim();
    if (!code) {
      toast.error('Enter an agent connection code');
      return;
    }
    setAgentCodeLoading(true);
    try {
      const res = await api.post('/api/agent-earn/player/apply-code', { code });
      setAgentCodeStatus(res.data || null);
      setAgentCodeDraft('');
      setAgentCodeOpen(false);
      await checkAuth?.();
      toast.success(`Connected to ${res.data?.agent?.display_name || res.data?.agent?.name || 'your agent'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not apply the agent connection code');
    } finally {
      setAgentCodeLoading(false);
    }
  };

  return (
    <div
      className="player-app fixed inset-0 z-[60] flex flex-col overflow-y-auto"
      style={{ background: 'var(--vl-bg, #000000)', color: '#fff' }}
      data-testid="profile-fullscreen"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3" style={{ background: 'var(--vl-bg, #000000)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          data-testid="profile-fs-close"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: PROFILE_ACCENT_TEXT }} />
        </button>
        <p className="display text-base font-extrabold tracking-wide" style={{ color: '#fff' }}>My Profile</p>
        <div className="w-10 h-10" aria-hidden="true" />
      </div>

      {/* Hero */}
      <div className="px-5 pt-7 pb-5 flex flex-col items-center text-center">
        <div className="relative">
          <span
            className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden"
            style={{
              background: avatarUrl ? '#0B0F0C' : `radial-gradient(circle at 30% 25%, #FFFFFF 0%, ${PROFILE_ACCENT_TEXT} 35%, ${PROFILE_ACCENT} 100%)`,
              boxShadow: `0 0 0 4px color-mix(in oklab, ${PROFILE_GOLD} 42%, transparent), 0 0 40px ${PROFILE_ACCENT_GLOW}, inset 0 0 16px rgba(255,255,255,0.3)`,
              border: `3px solid ${PROFILE_GOLD}`
            }}
          >
            {avatarUrl
              ? <SafeImage src={avatarUrl} alt="avatar" className="w-full h-full object-cover" loading="lazy" fallback={<CircleUser className="w-16 h-16" style={{ color: '#fff' }} />} />
              : <CircleUser className="w-16 h-16" style={{ color: '#fff' }} />}
          </span>
          <button
            onClick={openAvatarPicker}
            disabled={uploading}
            className={`absolute bottom-1 right-1 w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95 ${avatarTipVisible ? 'animate-pulse' : ''}`}
            style={{
              background: PROFILE_ACTION_BG,
              border: '3px solid #0B0618',
              boxShadow: '0 6px 14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.16)'
            }}
            data-testid="profile-fs-photo-upload"
            title="Choose or upload avatar"
          >
            {uploading ? <div className="spinner w-4 h-4" /> : <Camera className="w-4 h-4" style={{ color: '#fff' }} />}
          </button>
          {avatarTipVisible && (
            <div
              className="absolute -bottom-[58px] left-11 z-20 rounded-2xl border px-3.5 py-3 text-left shadow-2xl"
              style={{
                width: 'min(214px, calc(50vw + 12px))',
                borderColor: 'rgba(85, 225, 239, 0.5)',
                background: 'linear-gradient(135deg, #07314a, #071c2d)',
                boxShadow: '0 16px 36px rgba(0,0,0,0.48), 0 0 22px rgba(60,211,231,0.18)',
              }}
              role="status"
              data-testid="profile-avatar-tour-tip"
            >
              <i className="absolute -left-2 top-4 h-4 w-4 rotate-45 border-b border-l border-cyan-300/50 bg-[#082a40]" aria-hidden="true" />
              <button type="button" onClick={dismissAvatarTip} className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full text-slate-400" aria-label="Dismiss avatar tip"><X className="h-3.5 w-3.5" /></button>
              <strong className="block pr-5 text-[11px] font-extrabold text-cyan-200">Make your profile yours</strong>
              <span className="mt-1 block text-[10px] leading-relaxed text-slate-300">Tap the camera to upload a photo or choose a cool Slots Oceans avatar.</span>
            </div>
          )}
        </div>
        <div className={`flex items-center gap-2 ${avatarTipVisible ? 'mt-[72px]' : 'mt-4'}`}>
          {editingName ? (
            <div className="relative">
              <input
                ref={nameInputRef}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={saveProfileName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') {
                    setNameDraft(profileName);
                    setEditingName(false);
                  }
                }}
                maxLength={40}
                className="display w-[min(82vw,360px)] rounded-2xl px-4 py-2 pr-12 text-center text-2xl font-extrabold outline-none"
                style={{
                  background: PROFILE_ACCENT_DIM,
                  border: `1.5px solid ${PROFILE_ACCENT_STRONG_BORDER}`,
                  color: '#FFFFFF',
                  boxShadow: `0 0 0 4px ${PROFILE_ACCENT_DIM}`,
                }}
                placeholder="Set your name"
                data-testid="profile-fs-name-input"
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: PROFILE_ACCENT_TEXT }}
                aria-hidden
              >
                {savingName ? <div className="spinner w-4 h-4" /> : <BadgeCheck className="w-4 h-4" />}
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={startNameEdit}
              className="display text-2xl font-extrabold transition active:scale-[0.98] hover:brightness-110 rounded-xl px-1"
              style={{ color: visibleProfileName ? '#fff' : PROFILE_ACCENT_TEXT }}
              data-testid="profile-fs-name"
              title="Edit name"
            >
              {visibleProfileName || 'Set your name'}
            </button>
          )}
          <button
            onClick={startNameEdit}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full transition active:scale-95 hover:brightness-110"
            style={{
              background: PROFILE_ACCENT_DIM,
              border: `1px solid ${PROFILE_ACCENT_BORDER}`,
              color: '#fff',
              boxShadow: `0 4px 12px ${PROFILE_ACCENT_GLOW}, inset 0 1px 0 rgba(255,255,255,0.1)`
            }}
            data-testid="profile-fs-edit-bottom"
            aria-label="Edit name"
            title="Edit name"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
        <div
          className="mt-2 inline-flex items-center gap-1.5 rounded-full"
          style={{
            background: PROFILE_ACCENT_DIM,
            border: `1px solid ${PROFILE_ACCENT_BORDER}`,
            color: '#000000',
          }}
          data-testid="profile-fs-username-row"
        >
          <button
            type="button"
            onClick={copyUsername}
            className="inline-flex items-center gap-2 py-1.5 pl-3 pr-1.5 rounded-full transition active:scale-[0.98] hover:brightness-110"
            data-testid="profile-fs-username-copy"
            title={username ? 'Copy username' : 'Set username'}
          >
            <span className="text-sm font-bold leading-none">{usernameLabel}</span>
            <Copy className="w-3.5 h-3.5" />
          </button>
          {showVerifiedBadge && (
            <button
              type="button"
              onClick={showVerifiedUserToast}
              className="mr-2 rounded-full transition active:scale-95 hover:brightness-110"
              data-testid="profile-fs-verified-badge"
              title="Verified user"
              aria-label="Verified user"
            >
              <VerifiedUserBadge />
            </button>
          )}
        </div>
        {memberSince && (
          <span
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold"
            style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}`, color: '#000000' }}
            data-testid="profile-fs-member-since"
          >
            <CalendarDays className="w-3 h-3" /> Member Since {memberSince}
          </span>
        )}
      </div>

      {/* Personal progress */}
      <div className="px-5 pb-5 max-w-md w-full mx-auto" data-testid="profile-fs-personal">
        <p
          className="text-[11px] uppercase tracking-[0.22em] mb-2 px-1"
          style={{ color: PROFILE_ACCENT_TEXT, fontWeight: 800 }}
        >
          Personal
        </p>
        <button
          type="button"
          onClick={() => { tap(); setViewAllTiers(true); }}
          className="w-full rounded-[26px] p-4 text-left transition active:scale-[0.99] hover:brightness-110"
          style={{
            background: `linear-gradient(135deg, ${currentTier.color}22, rgba(5,12,20,0.92) 54%, ${PROFILE_ACCENT_SOFT})`,
            border: `1.5px solid color-mix(in oklab, ${currentTier.color} 54%, transparent)`,
            boxShadow: `0 18px 42px color-mix(in oklab, ${currentTier.color} 18%, transparent)`,
          }}
          data-testid="profile-tier-card"
        >
          <div className="flex items-center gap-3">
            <span
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl shrink-0 overflow-hidden"
              style={{
                background: currentTier.gradient,
                boxShadow: `0 10px 28px color-mix(in oklab, ${currentTier.color} 46%, transparent)`,
                border: '1px solid rgba(255,255,255,0.22)',
              }}
              aria-hidden
            >
              {currentTier.logoUrl ? (
                <SafeImage
                  src={resolveProfileUrl(currentTier.logoUrl)}
                  alt=""
                  className="w-full h-full object-cover"
                  fallback={<span className="display font-black text-white">{currentTier.icon}</span>}
                />
              ) : (
                <span className="display font-black text-white">{currentTier.icon}</span>
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <p className="display text-xl font-extrabold truncate" style={{ color: '#FFFFFF' }}>
                  {currentTier.name}
                </p>
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.14em]"
                  style={{ background: `${currentTier.color}22`, color: currentTier.color, border: `1px solid ${currentTier.color}66` }}
                >
                  Level {currentTier.level}
                </span>
              </div>
              <p className="mt-1 text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.62)' }}>
                {tiersLoading ? 'Loading tier progress...' : nextTier ? `${xpToNext.toLocaleString()} XP to ${nextTier.name}` : 'Top tier unlocked'}
              </p>
            </div>
            <ChevronDown className="w-5 h-5 -rotate-90 shrink-0" style={{ color: currentTier.color }} />
          </div>

          <div className="mt-4">
            <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${tierProgress}%`,
                  background: `linear-gradient(90deg, ${currentTier.color}, var(--vl-brand-primary, #D4AF37))`,
                  boxShadow: `0 0 18px ${currentTier.color}88`,
                }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 text-[11px] font-bold">
              <span style={{ color: 'rgba(255,255,255,0.58)' }}>{Math.round(currentPoints).toLocaleString()} XP</span>
              <span style={{ color: currentTier.color }}>{Math.round(tierProgress)}%</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-2xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: 'rgba(255,255,255,0.42)' }}>Next XP</p>
              <p className="mt-0.5 text-sm font-black" style={{ color: '#FFFFFF' }}>{nextTier ? nextMinXp.toLocaleString() : 'Max'}</p>
            </div>
            <div className="rounded-2xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: 'rgba(255,255,255,0.42)' }}>Refs Needed</p>
              <p className="mt-0.5 text-sm font-black" style={{ color: '#FFFFFF' }}>{nextTier ? referralsNeeded : 0}</p>
            </div>
          </div>

          {tierBenefitPreview.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {tierBenefitPreview.map((item, idx) => (
                <span key={`${item}-${idx}`} className="rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: `${currentTier.color}18`, color: 'rgba(255,255,255,0.82)', border: `1px solid ${currentTier.color}33` }}>
                  {String(item).replace(/^\[[^\]]+\]\s*/, '')}
                </span>
              ))}
            </div>
          )}
        </button>
      </div>

      {/* Account shortcuts */}
      <div className="px-5 pb-5 max-w-md w-full mx-auto" data-testid="profile-fs-account-shortcuts">
        <div className="mb-4 text-center">
          <h2
            className="text-2xl font-extrabold"
            style={{ color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif' }}
          >
            Security &amp; Account
          </h2>
          <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.62)' }}>
            Manage your account and keep it secure
          </p>
        </div>

        <SettingsCard
          icon={Mail}
          iconBg={PROFILE_ACCENT_GRADIENT}
          title="Email"
          subtitle={user?.email || 'Add your email'}
          onClick={onEditProfile}
          testid="profile-main-email-card"
        />
        <SettingsCard
          icon={Phone}
          iconBg="linear-gradient(135deg, #38BDF8, #2563EB)"
          iconClassName="w-6 h-6"
          title="Phone Number"
          subtitle={user?.phone_number || phoneRewardSubtitle}
          onClick={onEditPhone || onEditProfile}
          testid="profile-main-phone-card"
        />
        <SettingsCard
          icon={BadgeCheck}
          iconBg="linear-gradient(135deg, #D4AF37, #0EA5E9)"
          title="Apply Agent Connection Code"
          subtitle={agentCodeSubtitle}
          onClick={openAgentCodeModal}
          testid="profile-main-agent-code-card"
        />

        <p
          className="text-[11px] uppercase tracking-[0.22em] mt-6 mb-2 px-1"
          style={{ color: PROFILE_ACCENT_TEXT, fontWeight: 700 }}
        >
          Account
        </p>

        <SettingsCard
          icon={Lock}
          iconBg={PROFILE_ACCENT_GRADIENT}
          title="Change Password"
          subtitle="Update your password"
          onClick={onResetPassword}
          testid="profile-main-change-password-card"
        />
        <SettingsCard
          icon={LogOut}
          iconBg="linear-gradient(135deg, var(--vl-btn-logout-from, #EC4899), var(--vl-btn-logout-to, #BE185D))"
          title="Logout"
          subtitle="Sign out of your account"
          onClick={onLogout}
          testid="profile-main-logout-card"
        />
      </div>

      {/* View All Tiers modal */}
      {viewAllTiers && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
          onClick={() => setViewAllTiers(false)}
          data-testid="profile-fs-tiers-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="display text-xl font-extrabold flex items-center gap-2" style={{ color: '#fff' }}>
                <Crown className="w-5 h-5" style={{ color: '#FCD34D' }} /> All Tiers
              </h3>
              <button onClick={() => setViewAllTiers(false)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {[...tiers].reverse().map((t) => {
                const isCurrent = t.key === currentTier.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      const adminDoc = tierDocs[t.name.toLowerCase()] || null;
                      setTierDetail({ ...t, adminDoc });
                    }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition active:scale-[0.99] hover:brightness-110"
                    style={{
                      background: isCurrent ? PROFILE_ACCENT_DIM : 'rgba(255,255,255,0.03)',
                      border: isCurrent ? `1.5px solid ${PROFILE_ACCENT_BORDER}` : '1px solid rgba(255,255,255,0.06)',
                    }}
                    data-testid={`profile-fs-tier-row-${t.key}`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0 overflow-hidden" style={{ background: t.gradient }}>
                      {t.logoUrl ? (
                        <SafeImage
                          src={resolveProfileUrl(t.logoUrl)}
                          alt=""
                          className="w-full h-full object-cover"
                          fallback={<span className="display font-black text-white">{t.icon}</span>}
                        />
                      ) : (
                        <span className="display font-black text-white">{t.icon}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="display font-extrabold text-base" style={{ color: '#fff' }}>{t.name}</p>
                      <p className="text-xs" style={{ color: '#9CA3AF' }}>Level {t.level} · Unlocks at {t.minXp.toLocaleString()} XP · {t.minReferrals}+ refs</p>
                    </div>
                    {isCurrent && (
                      <span className="display text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: '#FCD34D', color: '#3F2A00' }}>Current</span>
                    )}
                    <ChevronDown className="w-4 h-4 -rotate-90 shrink-0 ml-1" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tier detail modal — admin-editable description per tier (Iter 145) */}
      {tierDetail && (
        <div
          className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
          onClick={() => setTierDetail(null)}
          data-testid="profile-fs-tier-detail"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-6 max-h-[88vh] overflow-y-auto relative"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          >
            <button
              onClick={() => setTierDetail(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}
              aria-label="Close tier detail"
              data-testid="profile-fs-tier-detail-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center mb-4">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl mb-3 overflow-hidden"
                style={{ background: tierDetail.gradient, boxShadow: `0 12px 30px ${tierDetail.crystal}55` }}
              >
                {tierDetail.logoUrl ? (
                  <SafeImage
                    src={resolveProfileUrl(tierDetail.logoUrl)}
                    alt=""
                    className="w-full h-full object-cover"
                    fallback={<span className="display font-black text-white">{tierDetail.icon}</span>}
                  />
                ) : (
                  <span className="display font-black text-white">{tierDetail.icon}</span>
                )}
              </div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>
                Tier · Level {tierDetail.level}
              </p>
              <h3 className="display text-3xl font-extrabold mt-1" style={{ color: '#fff' }}>
                {tierDetail.name}
              </h3>
              <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                Unlocks at {tierDetail.minXp.toLocaleString()} XP · {tierDetail.minReferrals}+ referrals
              </p>
              {tierDetail.dailyBonusPct > 0 && (
                <p className="text-xs mt-1" style={{ color: '#FCD34D' }}>{tierDetail.dailyBonusPct}% daily bonus boost</p>
              )}
            </div>

            {/* Admin-edited description (falls back to a friendly default).
                Supports inline markdown: **bold**, *italic*, [text](url),
                and \n linebreaks — rendered safely without dangerouslySetInnerHTML. */}
            <div
              className="text-sm sm:text-base leading-relaxed text-center"
              style={{ color: 'rgba(255,255,255,0.85)' }}
              data-testid="profile-fs-tier-detail-description"
            >
              {tierDetail.adminDoc?.description
                ? renderInlineMarkdown(tierDetail.adminDoc.description)
                : `Reach the ${tierDetail.name} tier to unlock exclusive benefits and rewards. Keep playing to climb higher and earn more.`}
            </div>

            {/* Benefits list — prefer admin-edited benefits; fall back to perks.
                Each benefit is parsed for a leading emoji or [shortcode] icon. */}
            {(((tierDetail.adminDoc?.benefits?.length || tierDetail.adminDoc?.perks?.length) || tierDetail.benefits?.length || tierDetail.perks?.length)) && (
              <div className="mt-5 space-y-2">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>
                  Benefits at this tier
                </p>
                {(tierDetail.adminDoc?.benefits?.length
                  ? tierDetail.adminDoc.benefits
                  : tierDetail.adminDoc?.perks?.length
                    ? tierDetail.adminDoc.perks
                    : tierDetail.benefits?.length
                      ? tierDetail.benefits
                      : tierDetail.perks || []).map((b, i) => (
                  <BenefitRow key={i} raw={b} accent={PROFILE_ACCENT} />
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setTierDetail(null)}
              className="mt-6 w-full h-11 rounded-2xl display font-extrabold transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: PROFILE_ACCENT_GRADIENT,
                color: 'var(--vl-btn-primary-text, #031A2A)',
                boxShadow: `0 10px 24px ${PROFILE_ACCENT_GLOW}`,
              }}
              data-testid="profile-fs-tier-detail-ack"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Agent connection-code modal */}
      {agentCodeOpen && (
        <div
          className="fixed inset-0 z-[82] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.68)', backdropFilter: 'blur(12px)' }}
          onClick={() => !agentCodeLoading && setAgentCodeOpen(false)}
          data-testid="profile-agent-code-modal"
        >
          <form
            onSubmit={submitAgentCode}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl p-5"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}`, boxShadow: `0 24px 60px ${PROFILE_ACCENT_GLOW}` }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-black" style={{ color: PROFILE_ACCENT_TEXT }}>
                  Agent Connection Code
                </p>
                <h3 className="mt-1 display text-2xl font-extrabold" style={{ color: '#FFFFFF' }}>
                  Connect to Your Referred Agent
                </h3>
                <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.62)' }}>
                  Add the connection code your agent shared before your signup window closes. This is separate from a player promo code.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !agentCodeLoading && setAgentCodeOpen(false)}
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}
                aria-label="Close agent connection code"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <label className="mt-5 block">
              <span className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Agent Connection Code
              </span>
              <input
                value={agentCodeDraft}
                onChange={(e) => setAgentCodeDraft(e.target.value.toUpperCase())}
                autoFocus
                maxLength={80}
                placeholder="#AG..."
                className="mt-2 w-full rounded-2xl px-4 py-4 text-lg font-black outline-none"
                style={{
                  background: PROFILE_ACCENT_DIM,
                  border: `1.5px solid ${PROFILE_ACCENT_STRONG_BORDER}`,
                  color: '#FFFFFF',
                  boxShadow: `0 0 0 4px ${PROFILE_ACCENT_DIM}`,
                }}
                data-testid="profile-agent-code-input"
              />
            </label>

            <button
              type="submit"
              disabled={agentCodeLoading}
              className="mt-5 w-full h-12 rounded-2xl display font-extrabold transition active:scale-[0.98] disabled:opacity-60"
              style={{
                background: PROFILE_ACCENT_GRADIENT,
                color: 'var(--vl-btn-primary-text, #031A2A)',
                boxShadow: `0 10px 24px ${PROFILE_ACCENT_GLOW}`,
              }}
              data-testid="profile-agent-code-submit"
            >
              {agentCodeLoading ? 'Applying...' : 'Apply Code'}
            </button>
          </form>
        </div>
      )}

      {avatarPickerOpen && (
        <OceanAvatarPicker
          currentAvatarUrl={avatarUrl}
          selectedId={selectedOceanAvatarId}
          busy={uploading}
          onSelect={selectOceanAvatar}
          onUpload={handleAvatarChange}
          onClose={() => !uploading && setAvatarPickerOpen(false)}
          fileInputRef={fileInputRef}
        />
      )}

      {/* Settings — full-screen Security & Account */}
      {settingsOpen && (
        <div
          className="player-app fixed inset-0 z-[70] flex flex-col animate-fade-in-up"
          style={{ background: 'var(--vl-bg, #000000)' }}
          data-testid="profile-fs-settings-panel"
        >
          <div className="relative px-5 pt-5 pb-4">
            <button
              onClick={() => onCloseSettings?.()}
              className="absolute left-5 top-5 w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95"
              style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
              data-testid="profile-fs-settings-close"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: PROFILE_ACCENT_TEXT }} />
            </button>
            <div className="flex justify-center pt-1">
              {branding.logo_url ? (
                <SafeImage
                  src={branding.logo_url}
                  alt={branding.brand_name || 'Brand'}
                  className="h-24 w-auto object-contain select-none"
                  draggable={false}
                  loading="lazy"
                  fallback={null}
                />
              ) : (
                <div
                  className="h-24 w-24 rounded-3xl flex items-center justify-center display text-3xl font-extrabold"
                  style={{
                    background: PROFILE_ACCENT_GRADIENT,
                    border: `1px solid ${PROFILE_ACCENT_BORDER}`,
                    color: '#FFFFFF',
                  }}
                >
                  {(branding.brand_name || 'B').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-10 px-5 max-w-md w-full mx-auto">
            <h1
              className="text-3xl font-extrabold text-center"
              style={{ color: '#FFFFFF', fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif' }}
            >
              Security &amp; Account
            </h1>
            <p className="text-center text-sm mt-1.5 mb-6" style={{ color: '#9CA3AF' }}>
              Manage your account and keep it secure
            </p>

            <SettingsCard
              icon={Mail}
              iconBg={PROFILE_ACCENT_GRADIENT}
              title="Email"
              subtitle={user?.email || 'Add your email'}
              onClick={onEditProfile}
              testid="settings-email-card"
            />
            <SettingsCard
              icon={Phone}
              iconBg="linear-gradient(135deg, #38BDF8, #2563EB)"
              iconClassName="w-6 h-6"
              title="Phone Number"
              subtitle={user?.phone_number || phoneRewardSubtitle}
              onClick={onEditPhone || onEditProfile}
              testid="settings-phone-card"
            />
            <SettingsCard
              icon={BadgeCheck}
              iconBg="linear-gradient(135deg, #D4AF37, #0EA5E9)"
              title="Apply Agent Connection Code"
              subtitle={agentCodeSubtitle}
              onClick={openAgentCodeModal}
              testid="settings-agent-code-card"
            />

            <p
              className="text-[11px] uppercase tracking-[0.22em] mt-6 mb-2 px-1"
              style={{ color: PROFILE_ACCENT_TEXT, fontWeight: 700 }}
            >
              Account
            </p>

            <SettingsCard
              icon={Lock}
              iconBg={PROFILE_ACCENT_GRADIENT}
              title="Change Password"
              subtitle="Update your password"
              onClick={onResetPassword}
              testid="settings-change-password-card"
            />
            <SettingsCard
              icon={LogOut}
              iconBg="linear-gradient(135deg, var(--vl-btn-logout-from, #EC4899), var(--vl-btn-logout-to, #BE185D))"
              title="Logout"
              subtitle="Sign out of your account"
              onClick={onLogout}
              testid="settings-logout-card"
            />
          </div>
        </div>
      )}
    </div>
  );
}
